import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Plus, Search, Send, Users, X, ArrowLeft } from 'lucide-react';
import { supabase } from '../supabase';

const getAdminEmail = () => {
  try { return JSON.parse(localStorage.getItem('workvan_internal_auth'))?.email; } catch(e) { return null; }
};

const TIER_COLORS = {
  affiliate: '#3b82f6',
  senior: '#8b5cf6',
  director: '#f59e0b',
  vp: '#ef4444',
};

function TierBadge({ tier }) {
  if (!tier) return null;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
      padding: '2px 8px', borderRadius: 4,
      background: (TIER_COLORS[tier] || '#6b7280') + '22',
      color: TIER_COLORS[tier] || '#6b7280',
    }}>{tier}</span>
  );
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMessageTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();

  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (isToday) return time;
  if (isYesterday) return `Yesterday ${time}`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + time;
}

export default function Messages() {
  const adminEmail = getAdminEmail();
  const [conversations, setConversations] = useState([]);
  const [affiliatesMap, setAffiliatesMap] = useState({});
  const [selectedConvoId, setSelectedConvoId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [affiliateSearch, setAffiliateSearch] = useState('');
  const [affiliateResults, setAffiliateResults] = useState([]);
  const [searchingAffiliates, setSearchingAffiliates] = useState(false);
  const [readMap, setReadMap] = useState({});
  const [lastMessages, setLastMessages] = useState({});
  const [mobileShowThread, setMobileShowThread] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load affiliates map
  const loadAffiliates = useCallback(async () => {
    const { data } = await supabase.from('affiliates').select('id, name, code, tier, email');
    const map = {};
    (data || []).forEach(a => { map[a.id] = a; });
    setAffiliatesMap(map);
    return map;
  }, []);

  // Load conversations
  const loadConversations = useCallback(async (affMap) => {
    const affiliates = affMap || affiliatesMap;
    const { data: convos } = await supabase
      .from('affiliate_conversations')
      .select('*')
      .order('updated_at', { ascending: false });

    // Load last message for each conversation
    const convoIds = (convos || []).map(c => c.id);
    const lastMsgs = {};
    if (convoIds.length > 0) {
      // Get most recent message per conversation
      for (const cid of convoIds) {
        const { data: msgs } = await supabase
          .from('affiliate_messages')
          .select('body, created_at, sender_admin_email, sender_affiliate_id')
          .eq('conversation_id', cid)
          .order('created_at', { ascending: false })
          .limit(1);
        if (msgs && msgs.length > 0) {
          lastMsgs[cid] = msgs[0];
        }
      }
    }
    setLastMessages(lastMsgs);

    // Load read status for admin
    if (adminEmail && convoIds.length > 0) {
      const { data: reads } = await supabase
        .from('affiliate_message_reads')
        .select('conversation_id, last_read_at')
        .eq('admin_email', adminEmail);
      const rm = {};
      (reads || []).forEach(r => { rm[r.conversation_id] = r.last_read_at; });
      setReadMap(rm);
    }

    setConversations(convos || []);
    setLoading(false);
  }, [affiliatesMap, adminEmail]);

  useEffect(() => {
    (async () => {
      const affMap = await loadAffiliates();
      await loadConversations(affMap);
    })();
  }, []); // eslint-disable-line

  // Load messages for selected conversation
  const loadMessages = useCallback(async (convoId) => {
    if (!convoId) return;
    setMessagesLoading(true);
    const { data } = await supabase
      .from('affiliate_messages')
      .select('*')
      .eq('conversation_id', convoId)
      .order('created_at', { ascending: true });
    setMessages(data || []);
    setMessagesLoading(false);

    // Mark as read
    if (adminEmail) {
      await supabase.from('affiliate_message_reads').upsert({
        conversation_id: convoId,
        admin_email: adminEmail,
        last_read_at: new Date().toISOString(),
      }, { onConflict: 'conversation_id,admin_email' });
      setReadMap(prev => ({ ...prev, [convoId]: new Date().toISOString() }));
    }
  }, [adminEmail]);

  useEffect(() => {
    if (selectedConvoId) {
      loadMessages(selectedConvoId);
    }
  }, [selectedConvoId, loadMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when convo selected
  useEffect(() => {
    if (selectedConvoId && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [selectedConvoId]);

  // Get participant display names
  const getConvoDisplayName = (convo) => {
    if (convo.is_group && convo.group_name) return convo.group_name;
    const names = (convo.participant_ids || [])
      .map(id => affiliatesMap[id]?.name || 'Unknown')
      .filter(Boolean);
    return names.join(', ') || 'Unknown';
  };

  const getConvoParticipants = (convo) => {
    return (convo.participant_ids || [])
      .map(id => affiliatesMap[id])
      .filter(Boolean);
  };

  // Check if conversation has unread messages
  const isUnread = (convo) => {
    const lastMsg = lastMessages[convo.id];
    if (!lastMsg) return false;
    // If admin sent the last message, not unread
    if (lastMsg.sender_admin_email) return false;
    const lastRead = readMap[convo.id];
    if (!lastRead) return true;
    return new Date(lastMsg.created_at) > new Date(lastRead);
  };

  // Send message
  const handleSend = async () => {
    if (!messageText.trim() || !selectedConvoId || sending) return;
    setSending(true);
    const body = messageText.trim();
    setMessageText('');

    const { error } = await supabase.from('affiliate_messages').insert({
      conversation_id: selectedConvoId,
      sender_admin_email: adminEmail,
      sender_affiliate_id: null,
      body,
    });

    if (!error) {
      await supabase.from('affiliate_conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', selectedConvoId);

      await loadMessages(selectedConvoId);
      await loadConversations();
    }
    setSending(false);
  };

  // Search affiliates for new message
  useEffect(() => {
    if (!affiliateSearch.trim()) {
      setAffiliateResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingAffiliates(true);
      const { data } = await supabase
        .from('affiliates')
        .select('id, name, code, tier, email')
        .ilike('name', `%${affiliateSearch}%`)
        .order('name')
        .limit(20);
      setAffiliateResults(data || []);
      setSearchingAffiliates(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [affiliateSearch]);

  // Start or open conversation with affiliate
  const startConversation = async (affiliate) => {
    // Check if a 1:1 conversation already exists with this affiliate
    const { data: existing } = await supabase
      .from('affiliate_conversations')
      .select('*')
      .contains('participant_ids', [affiliate.id])
      .eq('is_group', false);

    let convoId;
    if (existing && existing.length > 0) {
      // Find one that was created by admin or has admin messages
      const adminConvo = existing.find(c => c.created_by_admin);
      convoId = adminConvo ? adminConvo.id : existing[0].id;
    } else {
      // Create new conversation
      const { data: newConvo, error } = await supabase
        .from('affiliate_conversations')
        .insert({
          participant_ids: [affiliate.id],
          is_group: false,
          created_by_admin: adminEmail,
        })
        .select()
        .single();

      if (error) {
        console.error('Failed to create conversation:', error);
        return;
      }
      convoId = newConvo.id;
      await loadConversations();
    }

    setSelectedConvoId(convoId);
    setShowNewMessage(false);
    setAffiliateSearch('');
    setAffiliateResults([]);
    setMobileShowThread(true);
  };

  // Filter conversations by search
  const filteredConversations = conversations.filter(c => {
    if (!searchFilter.trim()) return true;
    const name = getConvoDisplayName(c).toLowerCase();
    return name.includes(searchFilter.toLowerCase());
  });

  const selectedConvo = conversations.find(c => c.id === selectedConvoId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 24px', borderBottom: '1px solid #2a2a2a',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <MessageSquare size={24} color="#ff6b35" />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#fff', margin: 0 }}>Messages</h1>
        </div>
      </div>

      {/* Two-panel layout */}
      <div style={{
        display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden',
      }}>
        {/* LEFT PANEL - Conversation List */}
        <div style={{
          width: 360, minWidth: 360, borderRight: '1px solid #2a2a2a',
          display: 'flex', flexDirection: 'column', background: '#0a0a0a',
          ...(mobileShowThread ? { '@media (maxWidth: 768px)': { display: 'none' } } : {}),
        }} className="messages-left-panel">
          {/* New Message + Search */}
          <div style={{ padding: 16, borderBottom: '1px solid #2a2a2a' }}>
            <button
              onClick={() => setShowNewMessage(true)}
              style={{
                width: '100%', padding: '10px 16px', background: '#ff6b35', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
                fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                marginBottom: 12,
              }}
            >
              <Plus size={16} /> New Message
            </button>
            <div style={{ position: 'relative' }}>
              <Search size={16} color="#666" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchFilter}
                onChange={e => setSearchFilter(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px 10px 36px', background: '#1a1a1a',
                  border: '1px solid #2a2a2a', borderRadius: 8, color: '#fff', fontSize: 14,
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* Conversation list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>Loading...</div>
            ) : filteredConversations.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
                {searchFilter ? 'No matching conversations' : 'No conversations yet'}
              </div>
            ) : (
              filteredConversations.map(convo => {
                const unread = isUnread(convo);
                const active = convo.id === selectedConvoId;
                const last = lastMessages[convo.id];
                const participants = getConvoParticipants(convo);
                return (
                  <div
                    key={convo.id}
                    onClick={() => { setSelectedConvoId(convo.id); setMobileShowThread(true); }}
                    style={{
                      padding: '14px 16px', cursor: 'pointer',
                      background: active ? '#1a1a1a' : 'transparent',
                      borderBottom: '1px solid #1a1a1a',
                      borderLeft: active ? '3px solid #ff6b35' : '3px solid transparent',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#141414'; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          {convo.is_group && (
                            <Users size={14} color="#666" />
                          )}
                          <span style={{
                            fontWeight: unread ? 700 : 500, color: '#fff', fontSize: 14,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {getConvoDisplayName(convo)}
                          </span>
                          {participants.length === 1 && <TierBadge tier={participants[0]?.tier} />}
                        </div>
                        {last && (
                          <div style={{
                            fontSize: 13, color: unread ? '#ccc' : '#666',
                            fontWeight: unread ? 500 : 400,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            maxWidth: 240,
                          }}>
                            {last.sender_admin_email ? 'You: ' : ''}
                            {last.body}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                        <span style={{ fontSize: 11, color: '#666' }}>
                          {formatTime(last?.created_at || convo.updated_at)}
                        </span>
                        {unread && (
                          <div style={{
                            width: 10, height: 10, borderRadius: '50%', background: '#ff6b35',
                          }} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT PANEL - Thread View */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column', background: '#0f0f0f',
          minHeight: 0,
        }} className="messages-right-panel">
          {!selectedConvo ? (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 12, color: '#666',
            }}>
              <MessageSquare size={48} color="#333" />
              <span style={{ fontSize: 16 }}>Select a conversation or start a new message</span>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div style={{
                padding: '14px 20px', borderBottom: '1px solid #2a2a2a',
                display: 'flex', alignItems: 'center', gap: 12, background: '#0a0a0a',
              }}>
                <button
                  onClick={() => { setMobileShowThread(false); setSelectedConvoId(null); }}
                  className="messages-back-btn"
                  style={{
                    background: 'none', border: 'none', color: '#888', cursor: 'pointer',
                    padding: 4, display: 'none',
                  }}
                >
                  <ArrowLeft size={20} />
                </button>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {selectedConvo.is_group && <Users size={16} color="#ff6b35" />}
                    <span style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>
                      {getConvoDisplayName(selectedConvo)}
                    </span>
                    {getConvoParticipants(selectedConvo).map(p => (
                      <TierBadge key={p.id} tier={p.tier} />
                    ))}
                  </div>
                  {!selectedConvo.is_group && getConvoParticipants(selectedConvo).length === 1 && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                      {getConvoParticipants(selectedConvo)[0]?.email || ''}
                    </div>
                  )}
                </div>
              </div>

              {/* Messages area */}
              <div style={{
                flex: 1, overflowY: 'auto', padding: '20px 20px 8px',
                display: 'flex', flexDirection: 'column', gap: 6,
              }}>
                {messagesLoading ? (
                  <div style={{ textAlign: 'center', color: '#666', padding: 40 }}>Loading messages...</div>
                ) : messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#666', padding: 40 }}>
                    No messages yet. Start the conversation below.
                  </div>
                ) : (
                  messages.map((msg, i) => {
                    const isAdmin = !!msg.sender_admin_email;
                    const senderName = isAdmin
                      ? (msg.sender_admin_email === adminEmail ? 'You' : msg.sender_admin_email)
                      : (affiliatesMap[msg.sender_affiliate_id]?.name || 'Unknown');

                    // Show date separator
                    const prevMsg = i > 0 ? messages[i - 1] : null;
                    const showDateSep = !prevMsg || new Date(msg.created_at).toDateString() !== new Date(prevMsg.created_at).toDateString();

                    return (
                      <React.Fragment key={msg.id}>
                        {showDateSep && (
                          <div style={{
                            textAlign: 'center', color: '#555', fontSize: 12,
                            margin: '12px 0 8px', position: 'relative',
                          }}>
                            <span style={{
                              background: '#0f0f0f', padding: '0 12px', position: 'relative', zIndex: 1,
                            }}>
                              {new Date(msg.created_at).toLocaleDateString('en-US', {
                                weekday: 'long', month: 'short', day: 'numeric',
                              })}
                            </span>
                          </div>
                        )}
                        <div style={{
                          display: 'flex',
                          justifyContent: isAdmin ? 'flex-end' : 'flex-start',
                          marginBottom: 2,
                        }}>
                          <div style={{ maxWidth: '70%', minWidth: 80 }}>
                            {/* Sender name */}
                            <div style={{
                              fontSize: 11, color: '#888', marginBottom: 3,
                              textAlign: isAdmin ? 'right' : 'left',
                              paddingLeft: isAdmin ? 0 : 12,
                              paddingRight: isAdmin ? 12 : 0,
                            }}>
                              {senderName}
                            </div>
                            <div style={{
                              padding: '10px 14px',
                              borderRadius: isAdmin ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                              background: isAdmin ? '#ff6b35' : '#1a1a1a',
                              color: isAdmin ? '#fff' : '#e0e0e0',
                              fontSize: 14, lineHeight: 1.5,
                              wordBreak: 'break-word',
                            }}>
                              {msg.body}
                            </div>
                            <div style={{
                              fontSize: 11, color: '#555', marginTop: 3,
                              textAlign: isAdmin ? 'right' : 'left',
                              paddingLeft: isAdmin ? 0 : 12,
                              paddingRight: isAdmin ? 12 : 0,
                            }}>
                              {formatMessageTime(msg.created_at)}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Compose bar */}
              <div style={{
                padding: '12px 20px', borderTop: '1px solid #2a2a2a', background: '#0a0a0a',
                display: 'flex', alignItems: 'flex-end', gap: 10,
              }}>
                <textarea
                  ref={inputRef}
                  value={messageText}
                  onChange={e => {
                    setMessageText(e.target.value);
                    // Auto-resize
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Type a message..."
                  rows={1}
                  style={{
                    flex: 1, padding: '10px 14px', background: '#1a1a1a',
                    border: '1px solid #2a2a2a', borderRadius: 12, color: '#fff',
                    fontSize: 14, outline: 'none', resize: 'none',
                    fontFamily: 'inherit', lineHeight: 1.5,
                    maxHeight: 120,
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={!messageText.trim() || sending}
                  style={{
                    width: 42, height: 42, borderRadius: '50%',
                    background: messageText.trim() ? '#ff6b35' : '#333',
                    border: 'none', cursor: messageText.trim() ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.15s', flexShrink: 0,
                    opacity: sending ? 0.6 : 1,
                  }}
                >
                  <Send size={18} color="#fff" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* New Message Modal */}
      {showNewMessage && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setShowNewMessage(false)}>
          <div
            style={{
              background: '#1a1a1a', borderRadius: 12, border: '1px solid #2a2a2a',
              width: 440, maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderBottom: '1px solid #2a2a2a',
            }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#fff' }}>New Message</h2>
              <button
                onClick={() => setShowNewMessage(false)}
                style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '16px 20px' }}>
              <div style={{ position: 'relative' }}>
                <Search size={16} color="#666" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  placeholder="Search affiliates by name..."
                  value={affiliateSearch}
                  onChange={e => setAffiliateSearch(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%', padding: '10px 12px 10px 36px', background: '#0a0a0a',
                    border: '1px solid #2a2a2a', borderRadius: 8, color: '#fff', fontSize: 14,
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', maxHeight: 400 }}>
              {searchingAffiliates ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>Searching...</div>
              ) : affiliateSearch.trim() && affiliateResults.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>No affiliates found</div>
              ) : !affiliateSearch.trim() ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                  Type a name to search affiliates
                </div>
              ) : (
                affiliateResults.map(aff => (
                  <div
                    key={aff.id}
                    onClick={() => startConversation(aff)}
                    style={{
                      padding: '12px 20px', cursor: 'pointer',
                      borderBottom: '1px solid #2a2a2a',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#252525'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ color: '#fff', fontWeight: 500, fontSize: 14, marginBottom: 2 }}>
                          {aff.name}
                        </div>
                        <div style={{ color: '#666', fontSize: 12 }}>
                          {aff.code} {aff.email ? `\u00B7 ${aff.email}` : ''}
                        </div>
                      </div>
                      <TierBadge tier={aff.tier} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .messages-left-panel {
            width: 100% !important;
            min-width: 0 !important;
            border-right: none !important;
            display: ${mobileShowThread ? 'none' : 'flex'} !important;
          }
          .messages-right-panel {
            display: ${mobileShowThread ? 'flex' : 'none'} !important;
          }
          .messages-back-btn {
            display: block !important;
          }
        }
      `}</style>
    </div>
  );
}
